import { Component, computed, effect, inject, input, OnDestroy, untracked } from '@angular/core';
import { rxResource, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { UiDividerComponent } from '@design-system';
import { OrderSide } from '@shared-types';
import { merge, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { KeyboardService } from '../../components/keyboard';
import { OrderFormConsumerService } from '../../events/order-form/order-form-consumer.service';
import { PrivateMessageEventConsumerService } from '../../events/private-message/private-message-event.consumer.service';
import { OrderFormInputData, OrderValidRangesType } from '../../models/order-form.model';
import { symbolIcon } from '../../models/symbol.model';
import { PortfolioService } from '../../services/portfolio.service';
import { MarketDataStore, resolveLastPrice } from '../../core/market-data';
import { MarketDepthBestLimitComponent } from '../market-depth-best-limit/market-depth-best-limit.component';
import {
	mapMarketViewMessageToDepthValues,
	mapSymbolOrderBookToDepthValues
} from '../market-depth-best-limit/models/market-depth.model';
import { MarketViewService } from '../market/services/market-view.service';
import { SymbolDetailCandleCardComponent } from '../symbol-details/components/symbol-detail-candle-card/symbol-detail-candle-card.component';
import { WalletStore } from '../wallet/data-access/wallet.store';
import { OrderFormActionsComponent } from './components/order-form-actions/order-form-actions.component';
import { OrderFormFieldsComponent } from './components/order-form-fields/order-form-fields.component';
import { OrderFormHeaderComponent } from './components/order-form-header/order-form-header.component';
import { OrderFormSummaryComponent } from './components/order-form-summary/order-form-summary.component';
import { OrderFormFacade } from './data-access/order-form.facade';
import { OrderFormFooterEventProducerService } from './events/order-form-footer-event-producer.service';
import { OrderFormHeaderEventConsumerService } from './events/order-form-header-event-consumer.service';
import { OrderFormHeaderEventProducerService } from './events/order-form-header-event-producer.service';
import { OrderFormHeaderSymbol } from './models/order-form-header.model';

@Component({
	selector: 'app-order-form',
	host: {
		class: 'flex h-full min-h-0 flex-col gap-1.5 overflow-hidden'
	},
	templateUrl: './order-form.component.html',
	imports: [
		OrderFormHeaderComponent,
		MarketDepthBestLimitComponent,
		OrderFormFieldsComponent,
		OrderFormSummaryComponent,
		OrderFormActionsComponent,
		UiDividerComponent,
		SymbolDetailCandleCardComponent
	],
	providers: [
		OrderFormFacade,
		OrderFormConsumerService,
		OrderFormHeaderEventProducerService,
		OrderFormHeaderEventConsumerService,
		OrderFormFooterEventProducerService,
		MarketViewService
	]
})
export class OrderFormComponent implements OnDestroy {
	private readonly orderFormFacade = inject(OrderFormFacade);
	private readonly walletStore = inject(WalletStore);
	private readonly orderFormConsumer = inject(OrderFormConsumerService);
	private readonly privateMessages = inject(PrivateMessageEventConsumerService);
	private readonly orderFormHeaderProducer = inject(OrderFormHeaderEventProducerService);
	private readonly orderFormHeaderConsumer = inject(OrderFormHeaderEventConsumerService);
	private readonly orderFormFooterProducer = inject(OrderFormFooterEventProducerService);
	private readonly marketViewService = inject(MarketViewService);
	private readonly marketData = inject(MarketDataStore);
	private readonly portfolioService = inject(PortfolioService);
	private readonly keyboardService = inject(KeyboardService);
	private readonly router = inject(Router);

	/**
	 * Payload attached by `OrderFormNavigatorService`. Captured in a field initializer because
	 * `currentNavigation()` is only populated while the navigation is in flight — by the time
	 * the route effect runs it is already null.
	 */
	private pendingNavigationInfo = this.readNavigationState();

	/** Path: /order-form/:isin/:orderSide — everything else rides on the navigation info. */
	readonly isin = input.required<string>();
	readonly orderSide = input.required<string>();
	readonly quantity = input<string>();

	readonly keyboardOpen = this.keyboardService.visibility;
	/** Pushes action buttons up so they stick directly above the keyboard. */
	readonly keyboardBottomOffset = this.keyboardService.bottomOffset;

	private readonly symbolResource = rxResource({
		params: () => this.isin(),
		stream: ({ params: isin }) => this.marketData.getSymbol(isin)
	});

	readonly symbolMeta = computed(() => (this.symbolResource.hasValue() ? this.symbolResource.value() : undefined));

	/** Sourced from the REST symbol snapshot — real isins carry no parseable contractId of their own. */
	private readonly contractId = computed(() => this.symbolMeta()?.contractId);

	/** Single market-view SSE subscription for the whole page, shared by every live child. */
	readonly liveMarketView = toSignal(
		toObservable(this.contractId).pipe(
			switchMap(contractId =>
				contractId === undefined ? of(undefined) : this.marketViewService.connectSingleContract(contractId)
			)
		),
		{ initialValue: undefined }
	);

	/** Baseline for change coloring — live tick first, REST snapshot while connecting or after a drop. */
	private readonly lastPrice = computed(() => resolveLastPrice(this.liveMarketView(), this.symbolMeta()));

	readonly previousClosePrice = computed(() => this.lastPrice().previousClose);

	readonly symbol = computed<OrderFormHeaderSymbol>(() => {
		const meta = this.symbolMeta();
		const { lastPrice, lastPriceChange, previousClose } = this.lastPrice();

		return {
			name: meta?.symbolShortName ?? '',
			lastPrice,
			lastPriceChange,
			lastPriceChangePercent: previousClose ? (lastPriceChange / previousClose) * 100 : 0,
			icon: meta?.symbol ? symbolIcon(meta.isin) : null
		};
	});

	/**
	 * Deposit holdings, matched to the symbol on `contractId`: both sides carry it, while
	 * `symbolinfo` reports the isin upper-cased and the portfolio promises no casing.
	 * A failed load leaves the holding empty — the form still trades, it just shows no asset.
	 *
	 * A `getremain` private message asks for a fresh portfolio read. An accepted order is not a
	 * fill, so refreshing on submit would still be premature.
	 */
	private readonly depositHoldings = toSignal(
		merge(of(undefined), this.privateMessages.depositPortfolioReloadRequested$).pipe(
			switchMap(() => this.portfolioService.getDepositPortfolio().pipe(catchError(() => of([]))))
		),
		{ initialValue: [] }
	);

	private readonly holding = computed(() =>
		this.depositHoldings().find(asset => asset.contractId === this.contractId())
	);

	/**
	 * Order limits straight off the REST symbol snapshot — price band and lot size.
	 *
	 * Four independent fields, not two pairs: a symbol can report `lot` without
	 * `maxOrderSize`, or `lowThreshold` without `highThreshold`. `0` is the absent
	 * marker, and the facade gates each bound on its own value — do not reintroduce a
	 * shared gate, or a missing maximum will silently drop the minimum beside it.
	 */
	private readonly validRanges = computed<OrderValidRangesType>(() => {
		const meta = this.symbolMeta();

		return {
			volume: { min: meta?.lot ?? 0, max: meta?.maxOrderSize ?? 0 },
			price: { min: meta?.lowThreshold ?? 0, max: meta?.highThreshold ?? 0 }
		};
	});

	/** Depth ladder — live tick once SSE has delivered one, otherwise the symbol's own REST order book. */
	readonly depthValues = computed(() => {
		const live = this.liveMarketView();
		return live ? mapMarketViewMessageToDepthValues(live) : mapSymbolOrderBookToDepthValues(this.symbolMeta());
	});

	constructor() {
		this.orderFormHeaderProducer.configureOrderFormHeader();
		this.orderFormFooterProducer.configureOrderFormFooter();
		this.orderFormFacade.setOnDismiss(() => this.dismissOrderForm());

		effect(() => {
			const meta = this.symbolMeta();
			if (!meta) {
				return;
			}

			this.orderFormHeaderProducer.configureOrderFormHeader({
				name: meta.symbolShortName,
				groupId: meta.marketGroupId
			});
		});

		// Opening the form is the one moment قدرت خرید has to be right — it is what the
		// percent slider sizes an order against. The store keeps the figure it had, so
		// this refreshes what is already on screen rather than blanking it, and it is
		// also the app's way back from a startup read that failed.
		this.walletStore.load();

		effect(() => {
			const data = this.buildFormDataFromRoute();
			untracked(() => this.orderFormFacade.setFormData(data));
		});

		// Best bid/ask reach the facade from the depth ladder's own order-form events.
		effect(() => {
			const ranges = this.validRanges();
			untracked(() => this.orderFormFacade.setValidRanges(ranges));
		});

		// The price grid the entered price has to land on; absent on the snapshot means no grid.
		effect(() => {
			const tickSize = this.symbolMeta()?.tickSize ?? 0;
			untracked(() => this.orderFormFacade.setTickSize(tickSize));
		});

		effect(() => {
			const holding = this.holding();
			// The snapshot reports each side's rate already split out of the round-trip figure.
			const symbolMeta = this.symbolMeta();
			// قدرت خرید of the گواهی سپرده wallet — the wallet a Deposit-market order is
			// funded from. Read from the store rather than fetched here, so the figure the
			// summary shows is the one the footer's bar shows.
			const buyingPower = this.walletStore.depositBuyingPower();
			untracked(() =>
				this.orderFormFacade.setAccountContext({
					currentAsset: holding?.remainingQuantity ?? 0,
					averageBuyPrice: holding?.averageBuyPrice ?? 0,
					buyingPower,
					buyCommissionRate: symbolMeta?.buyCommission ?? 0,
					sellCommissionRate: symbolMeta?.sellCommission ?? 0
				})
			);
		});

		toSignal(
			merge(this.orderFormConsumer.openRequest$, this.orderFormConsumer.updateRequest$).pipe(
				tap(data => this.orderFormFacade.setFormData(data))
			)
		);

		toSignal(
			this.orderFormHeaderConsumer.headerAction$.pipe(
				tap(event => {
					if (event?.data?.eventId === 'close') {
						this.orderFormFacade.handleAction('CLOSE');
					}
					if (event) {
						this.orderFormHeaderProducer.clearEventSnapshot(event.id);
					}
				})
			)
		);
	}

	ngOnDestroy(): void {
		this.orderFormHeaderProducer.resetHeader();
		this.orderFormFooterProducer.resetFooter();
	}

	onRetry(): void {
		this.symbolResource.reload();
	}

	/**
	 * Popping the history entry is the right dismissal when the form was opened from
	 * somewhere in the app. A deep link or a reload makes the form the session's first
	 * navigation, though, and then there is nothing to pop — `history.go(-1)` would either
	 * do nothing or leave the app, stranding the user on a cleared form with only the toast.
	 * The symbol the order was for is the nearest sensible place to land instead.
	 */
	private dismissOrderForm(): void {
		if (this.router.lastSuccessfulNavigation()?.id === 1) {
			this.router.navigate(['/symbol-details', this.isin()]);
			return;
		}

		history.go(-1);
	}

	private buildFormDataFromRoute(): OrderFormInputData {
		const data: OrderFormInputData = {
			isin: this.isin(),
			side: this.parseOrderSide(this.orderSide()),
			quantity: this.quantity() ? Number(this.quantity()) : undefined,
			isEdit: false,
			isCopy: false
		};

		// Navigation info wins over the URL — it carries fields the route cannot express
		// (price, quantity, isEdit, orderId, formType, validRanges) and comes from the
		// same dispatch as isin/side.
		return { ...data, ...this.consumeNavigationInfo() };
	}

	/** Applies to the first render only; later param changes must not replay a stale payload. */
	private consumeNavigationInfo(): OrderFormInputData | undefined {
		const info = this.pendingNavigationInfo;
		this.pendingNavigationInfo = undefined;
		return info;
	}

	/**
	 * Reads the payload from the in-flight navigation, falling back to `history.state` so a
	 * reload or a back/forward restores it too. Only the fields we put there are taken —
	 * `history.state` also carries Angular's own `navigationId`.
	 */
	private readNavigationState(): OrderFormInputData | undefined {
		const raw = this.router.currentNavigation()?.extras.state ?? history.state;

		if (!raw || typeof raw !== 'object') {
			return undefined;
		}

		const { isin, side, price, quantity, isEdit, isCopy, orderId, formType, validRanges } =
			raw as OrderFormInputData;
		const state: OrderFormInputData = {
			isin,
			side,
			price,
			quantity,
			isEdit,
			isCopy,
			orderId,
			formType,
			validRanges
		};

		for (const key of Object.keys(state) as (keyof OrderFormInputData)[]) {
			if (state[key] === undefined) {
				delete state[key];
			}
		}

		return Object.keys(state).length > 0 ? state : undefined;
	}

	private parseOrderSide(value: string): OrderSide {
		return Number(value) === OrderSide.Sell ? OrderSide.Sell : OrderSide.Buy;
	}
}
